import BaseService            from '@/core/services/BaseService';
import Core                   from '@/core/Core';
import Package                from '@/domains/Packages/Package';
import PackagesDAO            from '@/domains/Packages/PackagesDAO';
import ContextMenuItem        from '@/lib/components/VContextMenu/ContextMenuItem';

import type RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort       from '@/core/services/Rpc/RpcDataSort';


class PackagesService extends BaseService {

  /**
   * Получить вид упаковки по id.
   * Отдельного RPC packages.get нет — упаковка отдаётся через
   * packages.getList с фильтром по id.
   */
  static async get(id: number): Promise<Package | null> {
    const response = await PackagesDAO.getList({ id });
    const record   = response.data?.find((item: any) => item.id === id);
    return Core.utils.hydrate(record, Package.fromServer);
  }

  static async getListPaginated(
    params?: Parameters<typeof PackagesDAO.getList>[0] & {
      pagination?: RpcDataPagination;
      sort?: RpcDataSort;
    }
  ): Promise<{ data: Package[]; pagination: RpcDataPagination }> {
    const { pagination, sort, ...filters } = params ?? {};
    const response = await PackagesDAO.getList(filters, pagination, sort);

    return {
      data:       Core.utils.hydrateArray(response.data, Package.fromServer),
      pagination: response.meta.pagination,
    };
  }

  static async getList(
    params?: Parameters<typeof PackagesDAO.getList>[0]
  ): Promise<Package[]> {
    return (await this.getListPaginated(params)).data;
  }

  static async add(pkg: Package): Promise<boolean> {
    const response = await PackagesDAO.add(pkg);

    if (response.ok) {
      Core.showNotification.success('Упаковка создана');
    }

    return response.ok;
  }

  static async update(pkg: Package): Promise<boolean> {
    const response = await PackagesDAO.update(pkg);

    if (response.ok) {
      Core.showNotification.success('Упаковка обновлена');
    }

    return response.ok;
  }

  static async delete(id: number): Promise<boolean> {
    const response = await PackagesDAO.delete(id);

    if (response.ok) {
      Core.showNotification.success('Упаковка удалена');
    }

    return response.ok;
  }

  /**
   * Удаление с подтверждением. Используется из контекстного меню.
   * Старый delete(id) оставлен для массовых операций без UX.
   */
  static async deleteWithConfirmation(pkg: Package): Promise<boolean> {
    const confirmed = await Core.dialogs.confirm(
      'Удалить этот вид упаковки?',
      `${pkg.name}`,
    );

    if (!confirmed) return false;

    return await this.delete(pkg.id);
  }

  /**
   * Открыть карточку вида упаковки.
   * Промис резолвится по закрытию карточки.
   */
  static showCard(pkg: Package | null): Promise<boolean> {
    if (pkg) {
      return Core.dialogs.PackageCard.showAsPromise({ package: pkg });
    }
    else {
      return Core.dialogs.PackageCard.showAsPromise({});
    }
  }

  // ============================================================
  // Контекстное меню
  // ============================================================

  /**
   * Пункты контекстного меню вида упаковки для грида реестра и карточки.
   * Передаётся в VDataGridV2 / VDialogV3 пропом :context-menu.
   *
   * callback возвращает промис — грид дождётся завершения перед refresh.
   *   - showCard               → Promise<boolean> (резолв по закрытию карточки)
   *   - deleteWithConfirmation → Promise<boolean>
   *   - История действий       → void, список не меняет → refreshAfter: false
   *
   */
  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Открыть',
        'open_in_new',
        (pkg: Package) => { return this.showCard(pkg); },
        null,   // disabled
        false,  // hidden
        true,   // refreshAfter
        true,   // dividerAfter
      ),
      new ContextMenuItem(
        'История действий',
        'history',
        (pkg: Package) => { Core.dialogs.FeedDialog.show({ entityTypeName: 'package', entityId: pkg.id, showAddComment: false }); },
        null,   // disabled
        false,  // hidden
        false,  // refreshAfter
      ),
      new ContextMenuItem(
        'Удалить',
        'delete',
        (pkg: Package) => { return this.deleteWithConfirmation(pkg); },
      ),
    ];
  }
}

export default PackagesService;
